import { type ReactNode, type PropsWithChildren } from "react";
import { UNSAFE_PortalProvider as PortalProvider } from "react-aria";
import {
  TooltipTrigger as TooltipTriggerPrimitive,
  type TooltipTriggerComponentProps as TooltipTriggerPrimitiveProps,
  type TooltipProps as TooltipPrimitiveProps,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./TooltipV2.styled";
import { DEFAULT_OFFSET, DEFAULT_PLACEMENT } from "./TooltipV2.constants";
import { Arrow } from "./Arrow";
import { useOverlay } from "../OverlayContext";
import { Typography } from "../Typography";

export type TooltipV2Props = {
  children: ReactNode;
};

function TooltipV2({ children }: TooltipV2Props) {
  return children;
}

TooltipV2.Trigger = function TooltipV2Trigger(
  props: TooltipTriggerPrimitiveProps
) {
  /*
   * By default, the React Aria Tooltip waits 1.5 seconds before showing the content.
   * Adding a slight close delay just in case someone wants to select the tooltip content.
   *
   * https://react-spectrum.adobe.com/react-aria/Tooltip.html#props
   */
  return <TooltipTriggerPrimitive delay={0} closeDelay={100} {...props} />;
};

type TooltipV2ContentProps = PropsWithChildren &
  PropsWithOptionalTestID & {
    tooltipProps?: Omit<TooltipPrimitiveProps, "children">;
    maxWidth?: number;
  };

TooltipV2.Content = function TooltipV2Content(props: TooltipV2ContentProps) {
  const { tooltipProps, maxWidth, children, testID = "tooltip" } = props;

  const { tokens } = useDesignTokens();
  const { tooltip } = tokens;

  const { portalElement } = useOverlay();

  return (
    <PortalProvider getContainer={portalElement && (() => portalElement)}>
      <S.Tooltip
        offset={DEFAULT_OFFSET}
        placement={DEFAULT_PLACEMENT}
        {...tooltipProps}
        data-testid={testID}
      >
        <Arrow />

        <S.Container $maxWidth={maxWidth}>
          <Typography
            color={tooltip.color}
            variant={tooltip.typography.variant}
            fontFamily={tooltip.typography.fontFamily}
          >
            {children}
          </Typography>
        </S.Container>
      </S.Tooltip>
    </PortalProvider>
  );
};

export default TooltipV2;
